/**
 * IndustryMatrixToolbar — 2-row control bar for IndustryMappingPanel.
 * Row 1: industry picker · row search · column filter
 * Row 2: level filter · scope filter · row count · action buttons
 */
import React from 'react';
import { Search, Filter, RefreshCw, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { useT } from '../../i18n/I18nContext.jsx';

export function IndustryMatrixToolbar({
  selectedIndustry, industryList, onSelectIndustry,
  search, onSearch,
  industryFilter, onIndustryFilter,
  entityTypeFilter, onEntityTypeFilter,
  showOnly, onShowOnly,
  filteredCount,
  dirty, saving, loading,
  expandAll, collapseAll, revert, onReload,
  MAX_EXPAND,
}) {
  const t = useT();

  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6,
      padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0
    }}>
      {/* Row 1 */}
      <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        <select
          className="form-input im-ctrl"
          value={selectedIndustry}
          onChange={e => onSelectIndustry(e.target.value)}
          data-testid="industry-matrix-industry-picker"
          style={{ flex: '0 1 160px', minWidth: 0, fontWeight: 700 }}
          title={t('erp.industryMatrix.switchIndustry', 'Switch industry')}
        >
          {industryList.map(i => (
            <option key={i.IndustryCode} value={i.IndustryCode}>
              {i.IndustryName} ({i.IndustryCode})
            </option>
          ))}
        </select>

        <div style={{ position: 'relative', flex: '1 1 100px', minWidth: 0 }}>
          <Search size={10} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
          <input
            className="form-input im-ctrl"
            placeholder={t('erp.industryMatrix.searchRows', 'Search rows…')}
            value={search}
            onChange={e => onSearch(e.target.value)}
            data-testid="industry-matrix-search"
            style={{ paddingLeft: 22, width: '100%' }}
          />
        </div>

        <div style={{ position: 'relative', flex: '1 1 90px', minWidth: 0 }}>
          <Filter size={10} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
          <input
            className="form-input im-ctrl"
            placeholder={t('erp.industryMatrix.filterColumns', 'Filter columns…')}
            value={industryFilter}
            onChange={e => onIndustryFilter(e.target.value)}
            data-testid="industry-matrix-industry-filter"
            style={{ paddingLeft: 22, width: '100%' }}
          />
        </div>
      </div>

      {/* Row 2 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', minWidth: 0 }}>
        <select
          className="form-input im-ctrl"
          value={entityTypeFilter}
          onChange={e => onEntityTypeFilter(e.target.value)}
          data-testid="industry-matrix-entity-type"
          style={{ flex: '0 1 110px', minWidth: 0 }}
          title={t('erp.industryMatrix.levelFilter', 'Level filter')}
        >
          <option value="all">{t('erp.industryMatrix.allLevels', 'All Levels')}</option>
          <option value="module">{t('erp.industryMatrix.module', 'Module')}</option>
          <option value="submodule">{t('erp.industryMatrix.submodule', 'SubModule')}</option>
          <option value="screen">{t('erp.industryMatrix.screen', 'Screen')}</option>
        </select>

        <select
          className="form-input im-ctrl"
          value={showOnly}
          onChange={e => onShowOnly(e.target.value)}
          data-testid="industry-matrix-scope-filter"
          style={{ flex: '0 1 120px', minWidth: 0 }}
        >
          <option value="vertical_common">{t('erp.industryMatrix.vertCommon', 'Vert + Common')}</option>
          <option value="all">{t('erp.industryMatrix.allScopes', 'All Scopes')}</option>
          <option value="common">{t('erp.industryMatrix.common', 'Common')}</option>
          <option value="cross">{t('erp.industryMatrix.cross', 'Cross')}</option>
          <option value="vertical">{t('erp.industryMatrix.vertical', 'Vertical')}</option>
        </select>

        <span style={{ flex: '1 1 60px', fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {filteredCount} {t('erp.industryMatrix.modules', 'modules')}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 3, flex: '0 0 auto' }}>
          <button
            className="erp-btn im-icon-btn" onClick={expandAll}
            disabled={filteredCount > MAX_EXPAND}
            data-testid="industry-matrix-expand-all"
            title={filteredCount > MAX_EXPAND
              ? `Filter to ≤${MAX_EXPAND} modules to use Expand All (currently ${filteredCount})`
              : `Expand all ${filteredCount} module(s)`}
          ><ChevronDown size={12} /></button>
          <button className="erp-btn im-icon-btn" onClick={collapseAll}
            data-testid="industry-matrix-collapse-all"
            title={t('erp.industryMatrix.collapseAll', 'Collapse all')}
          ><ChevronRight size={12} /></button>
          <button className="erp-btn im-icon-btn" onClick={revert}
            data-testid="industry-matrix-revert"
            disabled={!dirty || saving}
            title={t('erp.industryMatrix.revert', 'Revert changes')}
          ><AlertTriangle size={12} /></button>
          <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 1px' }} />
          <button className="erp-btn im-icon-btn" onClick={onReload}
            disabled={saving}
            data-testid="industry-matrix-reload"
            title={t('erp.industryMatrix.reload', 'Reload from server')}
          ><RefreshCw size={12} className={loading ? 'spin' : ''} /></button>
        </div>
      </div>
    </div>
  );
}
